import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { DownloadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { connect } from 'react-redux'
import { setLoading, setSnackInfo } from '../redux/actions/appAction';
import HttpService from '../services/httpService';
import TextField from '@material-ui/core/TextField';
import { useSelector } from 'react-redux';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText  } from '@material-ui/core';
import { ROLENAME} from '../utils/index';
import { Checkbox, FormControlLabel, Radio } from '@mui/material';
import Autocomplete from '@material-ui/lab/Autocomplete';

const useStyles = makeStyles((theme) => ({
	table: {
		minWidth: 700,
	},
	formControl: {
		margin: theme.spacing(3),
	},
	root: {
		display: 'flex',
	},
	textField: {
		width: "100px"
	},
}));

const StyledTableCell = withStyles((theme) => ({
	head: {
		backgroundColor: "#ccc",
		color: theme.palette.common.black,
	},
	body: {
		fontSize: 14,
	},
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
	root: {
		'&:nth-of-type(odd)': {
			backgroundColor: theme.palette.action.hover,
		},
	},
}))(TableRow);

const PoDetailsModal = (props) => {
	const classes = useStyles();
	const [poDetails, setPoDetails] = useState([])
	const { userDetails } = useSelector(state => state.user)
	const [newRequestedQty,setNewRequestedQty] = useState([]);
	const [newRequestedRate,setNewRequestedRate] = useState([]);
	const originalRates = useRef([]);

	const [rejectOpenModel, setRejectOpenModel] = useState(false);
	const [remarks, setRemarks] = useState("");
	const [rejectionInfo, setRejectionInfo] = useState({
		procurementId: "",
		index: "",
	});
	const GST_PERCENTS = ["5", "12", "18", "28"];
	const [gstRequired, setGstRequired] = useState(false);
	const [gstDetails, setGstDetails] = useState({ gst: "", rate: "" });
	const [discount, setDiscount] = useState("");
	const [extraCharge, setExtraCharge] = useState("freight");
	const [chargeAmount, setChargeAmount] = useState("");

	const [isAutomationPo, setIsAutomationPo] = useState(false);
	const [automationVendors, setAutomationVendors] = useState([]);
	const [selectedVendor, setSelectedVendor] = useState(null);
	const [vendorLoading, setVendorLoading] = useState(false);

	const isAutomationSource = poDetails?.isMoQGenerated === true
		&& poDetails?.sourceType === "Automation OPEN_PO";
	const vendorMissing = isAutomationPo && !selectedVendor?._id;

	useEffect(() => {
		const procurementId = props?.PODETAILS?._id;
		setSelectedVendor(null);
		setAutomationVendors([]);
		setIsAutomationPo(false);
		const existing = props?.PODETAILS || {};
		const existingGst = Array.isArray(existing.gstDetails) ? existing.gstDetails[0] : null;
		setGstRequired(existing.gstRequired === true);
		setGstDetails({
			gst: existingGst?.gst || "",
			rate: existingGst?.rate ? String(existingGst.rate) : "",
		});
		setDiscount(existing.discount ? String(existing.discount) : "");
		setChargeAmount(existing.freight ? String(existing.freight) : "");
		setExtraCharge("freight");

		if (!procurementId || props?.PODETAILS?.isMoQGenerated !== true
			|| props?.PODETAILS?.sourceType !== "Automation OPEN_PO") return;

		let cancelled = false;
		(async () => {
			setVendorLoading(true);
			try {
				const response = await HttpService.getAutomationPoVendors(procurementId);
				if (cancelled) return;
				const data = response?.data?.data || {};
				setIsAutomationPo(Boolean(data.isAutomationPo));
				setAutomationVendors(Array.isArray(data.vendors) ? data.vendors : []);
			} catch (error) {
				if (cancelled) return;
				console.error(error);
				props.setSnackInfo("Couldn't load vendors for this automation PO", "error");
			}
			if (!cancelled) setVendorLoading(false);
		})();

		return () => { cancelled = true; };
	}, [props?.PODETAILS?._id]);

	const generateAutomationPo = async (procurementId) => {
		if (!isAutomationPo) return false;
		try {
			await HttpService.generatePO(procurementId);
			props.setSnackInfo("PO document generated", "success");
			return true;
		} catch (error) {
			console.error(error);
			props.setSnackInfo(
				(error?.response?.data?.message || error?.response?.data || "PO approved, but the document could not be generated")
				+ " — you can still use Generate PO from the list",
				"warning",
			);
			return false;
		}
	};

	const notifyAutomationVendor = async (procurementId) => {
		if (!isAutomationPo) return;
		try {
			const response = await HttpService.notifyAutomationPoVendor(procurementId);
			const data = response?.data?.data || {};
			props.setSnackInfo(
				data.emailSent
					? `PO emailed to ${data.vendor?.name || "the vendor"}${data.poLink ? " with the PO copy" : ""}`
					: `Vendor not emailed — ${data.emailError || "unknown reason"}`,
				data.emailSent ? "success" : "warning",
			);
		} catch (error) {
			console.error(error);
			props.setSnackInfo(
				error?.response?.data?.message || "PO approved, but the vendor could not be emailed",
				"warning",
			);
		}
	};

	const buildChargesPayload = () => {
		const charges = { gstRequired: gstRequired };

		if (gstRequired) {
			charges.gstDetails = { gst: gstDetails.gst, rate: gstDetails.rate };
		} else {
			charges.gstDetails = { gst: "", rate: "" };
		}

		if (String(discount).trim() !== "") charges.discount = Number(discount);
		if (String(chargeAmount).trim() !== "") charges[extraCharge] = Number(chargeAmount);

		return charges;
	};

	const applyVendorRates = (vendor) => {
		const po = { ...poDetails };
		if (!Array.isArray(po.materials)) return;

		const rates = [...newRequestedRate];

		po.materials = po.materials.map((line, i) => {
			const materialId = String(line?.materialId?._id || line?.materialId || '');

			if (!vendor?._id) {
				const original = originalRates.current[i];
				if (original) {
					rates[i] = original.requestedRate;
					return { ...line, pricePerUnit: original.pricePerUnit, requestedPrice: original.requestedPrice };
				}
				return line;
			}

			const match = (vendor.materials || []).find(
				(m) => String(m.materialId) === materialId,
			);
			if (!match || !(Number(match.pricePerUnit) > 0)) return line;

			rates[i] = Number(match.pricePerUnit);
			return {
				...line,
				pricePerUnit: String(match.pricePerUnit),
				requestedPrice: String(match.pricePerUnit),
			};
		});

		setPoDetails(po);
		setNewRequestedRate(rates);
	};

	const handleVendorChange = (vendor) => {
		setSelectedVendor(vendor);
		if (isAutomationPo) applyVendorRates(vendor);
	};

	const saveVendorDecision = async (procurementId, action) => {
		if (!isAutomationPo || !selectedVendor?._id) return;
		try {
			await HttpService.saveAutomationPoVendor(procurementId, {
				vendorId: selectedVendor._id,
				action: action,
				charges: buildChargesPayload(),
			});
			props.setSnackInfo(`Vendor ${selectedVendor.name} saved against this PO`, "success");
		} catch (error) {
			console.error(error);
			props.setSnackInfo(
				error?.response?.data?.message || "PO actioned, but the vendor could not be saved",
				"error",
			);
		}
	};

	// Function to reject the procurement
	const rejectProcurementConfirm = async (procurementId, index) => {
		props.setLoading(true);
		try {
			if (remarks === "") {
				props.setSnackInfo("Please Enter your remark", "error");
			} else {
				const obj = [];
				let data = {
					materialElementId: poDetails?.materials[rejectionInfo?.index]?._id,
					materialId:
						poDetails?.materials[rejectionInfo?.index]?.materialId?._id,
					pricePerUnit:
						poDetails?.materials[rejectionInfo?.index]?.pricePerUnit,
					requestedPrice:
						poDetails?.materials[rejectionInfo?.index]?.requestedPrice,
					requestedQuantity:
						poDetails?.materials[rejectionInfo?.index]?.requestedQuantity,
					projectId: props?.SELECTEDPROJECT?._id ?? poDetails?.projectId?._id,
					projectMaterialId:
						poDetails?.materials[rejectionInfo?.index]?.projectMaterialId,
					quantity: poDetails?.materials[rejectionInfo?.index]?.quantity,
					scopeId: poDetails?.materials[rejectionInfo?.index]?.scopeId?._id,
					remarks: remarks,
				};
				obj.push(data);
				const result = await HttpService.rejectProcurement(
					rejectionInfo?.procurementId,
					obj
				);
				if (result.status === 200) {
					props.setSnackInfo("PO Rejected", "success");
					await saveVendorDecision(rejectionInfo?.procurementId, "reject");
					props.REFRESH(rejectionInfo?.procurementId);
				}
				setRejectOpenModel(false);
				setRejectionInfo({procurementId:"",index:""});
				setRemarks("");
			}

		} catch (error) {
			console.error(error);
			props.setSnackInfo(error?.response?.data, "error");
		}
		props.setLoading(false);
	};

	// Function to handle checkbox checking for "approveCurrentPoCheck"
	const approveCurrentPoCheckHandler = (event,index)=>{
		let arr = {...poDetails};
		arr.materials[index].approveForCurrentPo = arr.materials[index].approveForCurrentPo;
		setPoDetails(arr);
	}

	// Function to handle checkbox checking for "updateStdRateCheck"
	const updateStdRateCheckHandler = (event,index)=>{
		let arr = {...poDetails};
		arr.materials[index].updateStandardRateOfMaterial = !arr.materials[index].updateStandardRateOfMaterial;
		setPoDetails(arr);
	}

	useEffect(() => {
		setPoDetails(props?.PODETAILS);
		let newRequestedQtyArr = [...newRequestedQty]; 
		let newRequestedRateArr = [...newRequestedRate];
		for(let i = 0; i < props.PODETAILS.materials.length; i++){
			newRequestedQtyArr[i] = +props?.PODETAILS?.materials[i]?.originallyRequestedQty;
			newRequestedRateArr[i] = +props?.PODETAILS?.materials[i]?.originallyRequestedRate;
		}
		setNewRequestedQty(newRequestedQtyArr);
		setNewRequestedRate(newRequestedRateArr);
		originalRates.current = props.PODETAILS.materials.map((line, i) => ({
			pricePerUnit: line?.pricePerUnit,
			requestedPrice: line?.requestedPrice,
			requestedRate: newRequestedRateArr[i],
		}));
	}, [props?.PODETAILS])

	const approveProcurement = async (procurementId, index) => {
		if (vendorMissing) {
			props.setSnackInfo("Select a vendor before approving this automation PO", "error");
			return;
		}
		props.setLoading(true)
		try {
			const obj = []
			let data = {
				materialElementId: poDetails?.materials[index]?._id,
				materialId: poDetails?.materials[index]?.materialId?._id,
				pricePerUnit: poDetails?.materials[index]?.pricePerUnit,
				requestedPrice: poDetails?.materials[index]?.requestedPrice,
				requestedQuantity: poDetails?.materials[index]?.requestedQuantity,
				projectId: props?.SELECTEDPROJECT?._id ?? poDetails?.projectId?._id,
				projectMaterialId: poDetails?.materials[index]?.projectMaterialId,
				quantity: poDetails?.materials[index]?.quantity,
				scopeId: poDetails?.materials[index]?.scopeId?._id,
				approveForCurrentPo:poDetails?.materials[index]?.approveForCurrentPo,
				updateStandardRateOfMaterial:poDetails?.materials[index]?.updateStandardRateOfMaterial,
				approvedQuantity : newRequestedQty[index],
				approvedRate : newRequestedRate[index],
				firstApprover : userDetails?._id,
			}
			obj.push(data)
			if(poDetails?.materials[index].approveForCurrentPo && !poDetails?.materials[index].updateStandardRateOfMaterial){
				data.requestedPrice = data.approvedRate;
				data.requestedQuantity = data.approvedQuantity;
				data.finalApprover = data.firstApprover;
				let objNew = [];
				objNew.push(data);
				const result = await HttpService.approveProcurement(procurementId, objNew);
				if (result.status === 200) {
					props.setSnackInfo('PO Approved', "success")
					await saveVendorDecision(procurementId, "approve")
					await generateAutomationPo(procurementId)
					await notifyAutomationVendor(procurementId)
					props.REFRESH(procurementId)
				}
			}
			if(poDetails?.materials[index].approveForCurrentPo && poDetails?.materials[index].updateStandardRateOfMaterial){
				const result = await HttpService.approveProcurementByOperationHead(procurementId, obj)
				if (result.status === 200) {
					props.setSnackInfo(result?.data?.message || "Approval for standard price updation sent", "info")
					await saveVendorDecision(procurementId, "approve")
					props.REFRESH(procurementId)
				}
			}
		} catch (error) {
			console.error(error)
			props.setSnackInfo(error?.response?.data, "error")
		}
		props.setLoading(false)
	}

	const rejectProcurement = async (procurementId, index) => {
		if (vendorMissing) {
			props.setSnackInfo("Select a vendor before rejecting this automation PO", "error");
			return;
		}
		props.setLoading(true)
		/*
		try {
			const obj = []
			let data = {
				materialElementId: poDetails?.materials[index]?._id,
				materialId: poDetails?.materials[index]?.materialId?._id,
				pricePerUnit: poDetails?.materials[index]?.pricePerUnit,
				requestedPrice: poDetails?.materials[index]?.requestedPrice,
				requestedQuantity: poDetails?.materials[index]?.requestedQuantity,
				projectId: props?.SELECTEDPROJECT?._id ?? poDetails?.projectId?._id,
				projectMaterialId: poDetails?.materials[index]?.projectMaterialId,
				quantity: poDetails?.materials[index]?.quantity,
				scopeId: poDetails?.materials[index]?.scopeId?._id
			}
			obj.push(data)
			const result = await HttpService.rejectProcurement(procurementId, obj)
			if (result.status === 200) {
				props.setSnackInfo('PO Rejected', "success")
				props.REFRESH(procurementId)
			}
		} catch (error) {
			console.error(error)
			props.setSnackInfo(error?.response?.data, "error")
		}
		*/
		setRejectOpenModel(true);
		setRejectionInfo({ procurementId: procurementId, index: index });
		props.setLoading(false)
	}

	const handleRequestQunatityChange = (e, i) => {
		// let po = { ...poDetails }
		// po.materials[i].requestedQuantity = e.target.value
		// setPoDetails(po)

		let newRequestedQtyArr = [...newRequestedQty];
		newRequestedQtyArr[i] = e.target.value;
		setNewRequestedQty(newRequestedQtyArr);
	}

	const handelRequestPriceChange = (e, i) => {
		// let po = { ...poDetails }
		// po.materials[i].requestedPrice = e.target.value
		// setPoDetails(po)
		
		let newRequestedRateArr = [...newRequestedRate];
        newRequestedRateArr[i] = e.target.value;
        setNewRequestedRate(newRequestedRateArr);
	}

	const getMaskedPODocument = async (data) => {
		try {
			const result = await HttpService.generateMaskedPo(data._id)
			window.open(result.data.link)
		} catch (error) {
			console.log(error, "error")
		}
	}

	const [deleteOpenModel, setDeleteOpenModel] = useState(false)

	const removePOForProject = () => {
		setDeleteOpenModel(true)
	}

	const deletePoConfirm = async () => {
		props.setLoading(true)
		try {
			let response = await HttpService.removePO(poDetails._id)
			props.setSnackInfo(response?.data ?? "Something went wrong", "success")
			setDeleteOpenModel(false)
			props.VIEWALL(props.TITLE !== "Approve PO" ? 'PO/MTR Raised/Requested' : "TEST")
		} catch (error) {
			console.error(error)
			props.setSnackInfo(error?.response?.data?.message ?? "Error", "error")
		}
		props.setLoading(false)
	}

	return (
		<div>
			<div className='modal-header' style={{ justifyContent: "space-between" }}>
				<div style={{ width: "25%" }}>
					<b>OrderID: </b>
					<span>{poDetails.orderId}</span>
				</div>
				<div style={{ width: "25%" }}>
					<b>Created At: </b>
					<span>{moment(poDetails.createdAt).format('DD-MM-YYYY')}</span>
				</div>
				<div style={{ width: "25%" }}>
					<b>Project Code: </b>
					<span>{poDetails.projectId?.code ?? "OPEN"}</span>
				</div>
			</div>
			<div className='modal-header' style={{ justifyContent: "space-between" }}>
				<div style={{ width: "25%" }}>
					<b>Client: </b>
					<span>{poDetails.projectId?.clientName ?? "PROCUREMENT"}</span>
				</div>
				{poDetails.status !== "Sent for Approval" && poDetails?.poLink !== undefined &&
					<div style={{ width: "25%" }}>
						<span className="btn"onClick={() => window.open(poDetails?.poLink)}>Download PO <DownloadOutlined /></span>
						<br /><br />
						{
							poDetails?.maskedPoLink ?
								<span className="btn" onClick={() => window.open(poDetails?.maskedSoLink)}> Download Masked PO <DownloadOutlined /></span>
								:
								<span className="btn" onClick={() => getMaskedPODocument(poDetails)}> Download Masked PO <DownloadOutlined /></span>
						}
					</div>
				}
				<div style={{ width: "25%" }}>
					<span className="btn" onClick={() => props.VIEWALL(props.TITLE !== "Approve PO" ? 'PO/MTR Raised/Requested' : "TEST")}>View All <EyeOutlined /></span>
					<br /><br />
					{userDetails?.roles?.find(role => role.name == ROLENAME.OperationHead) && <span className="btn" onClick={() => removePOForProject()}>Delete PO<DeleteOutlined /> </span>}
				</div>
			</div>
			{isAutomationSource && poDetails?.status !== "Approved" && poDetails?.status !== "Rejected" && (
				<div className='modal-header' style={{ justifyContent: "flex-start", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
					<div style={{ minWidth: 340 }}>
						<Autocomplete
							value={selectedVendor}
							onChange={(e, v) => handleVendorChange(v)}
							options={automationVendors}
							loading={vendorLoading}
							getOptionLabel={(option) => option?.name || ""}
							getOptionSelected={(option, value) => option?._id === value?._id}
							renderInput={(params) => (
								<TextField
									{...params}
									label={vendorLoading ? "Loading vendors…" : "Select Vendor *"}
									variant="outlined"
									error={vendorMissing}
									helperText={vendorMissing
										? "Required — approve and reject stay disabled until a vendor is chosen"
										: " "}
								/>
							)}
						/>
					</div>
					{selectedVendor?._id && (
						<div style={{ fontSize: 13, color: "#555" }}>
							<div><b>Email:</b> {selectedVendor.email || <span style={{ color: "#c62828" }}>none on record</span>}</div>
							{selectedVendor.mobile && <div><b>Mobile:</b> {selectedVendor.mobile}</div>}
						</div>
					)}
					{!vendorLoading && isAutomationPo && automationVendors.length === 0 && (
						<div style={{ fontSize: 13, color: "#c62828" }}>
							No vendor is mapped to this PO's material — map one under Vendor Materials first.
						</div>
					)}
				</div>
			)}
			{isAutomationSource && poDetails?.status !== "Approved" && poDetails?.status !== "Rejected" && (
				<div style={{ border: "1px solid #eee", borderRadius: 6, padding: 12, marginBottom: 14 }}>
					<div style={{ fontWeight: 600, marginBottom: 10 }}>
						Add GST and extra charge details
						<span style={{ fontWeight: 400, fontSize: 12, color: "#777", marginLeft: 8 }}>
							(optional — saved with the PO when you approve)
						</span>
					</div>

					<div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
						<TextField
							label="Discount"
							type="number"
							variant="outlined"
							size="small"
							value={discount}
							onChange={(e) => setDiscount(e.target.value)}
							style={{ width: 200 }}
						/>
						<span style={{ border: "1.5px solid #ccc", padding: "1px 10px", borderRadius: 4 }}>
							<Checkbox
								checked={gstRequired}
								onChange={(e) => setGstRequired(e.target.checked)}
								inputProps={{ "aria-label": "GST required" }}
							/>
							GST Required
						</span>
					</div>

					{gstRequired && (
						<div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
							<span style={{ border: "1.5px solid #ccc", padding: "0 10px", borderRadius: 4 }}>
								<FormControlLabel
									control={<Radio checked={gstDetails.gst === "SGST"} onChange={() => setGstDetails({ ...gstDetails, gst: "SGST" })} />}
									label="CGST/SGST"
								/>
								<FormControlLabel
									control={<Radio checked={gstDetails.gst === "IGST"} onChange={() => setGstDetails({ ...gstDetails, gst: "IGST" })} />}
									label="IGST"
								/>
							</span>
							<Autocomplete
								value={gstDetails.rate || null}
								onChange={(e, value) => setGstDetails({ ...gstDetails, rate: value || "" })}
								options={GST_PERCENTS}
								getOptionLabel={(option) => `${option}%`}
								style={{ width: 240 }}
								renderInput={(params) => (
									<TextField
										{...params}
										label={`Select ${gstDetails.gst || "GST"} percent`}
										variant="outlined"
										size="small"
									/>
								)}
							/>
						</div>
					)}

					<div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
						<span style={{ border: "1.5px solid #ccc", padding: "0 10px", borderRadius: 4 }}>
							<FormControlLabel
								control={<Radio checked={extraCharge === "freight"} onChange={() => setExtraCharge("freight")} />}
								label="Freight"
							/>
							<FormControlLabel
								control={<Radio checked={extraCharge === "transportationCharge"} onChange={() => setExtraCharge("transportationCharge")} />}
								label="Transport"
							/>
						</span>
						<TextField
							label={extraCharge === "freight" ? "Freight charge" : "Transportation charge"}
							type="number"
							variant="outlined"
							size="small"
							value={chargeAmount}
							onChange={(e) => setChargeAmount(e.target.value)}
							style={{ width: 240 }}
						/>
					</div>
				</div>
			)}
			<TableContainer component={Paper}>
				<Table className={classes.table} size="small" aria-label="customized table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Sl No</StyledTableCell>
							<StyledTableCell>Material Code</StyledTableCell>
							<StyledTableCell>Scope</StyledTableCell>
							<StyledTableCell>Status</StyledTableCell>
							<StyledTableCell>Vendor Rate</StyledTableCell>
							<StyledTableCell>Requested Rate</StyledTableCell>
							<StyledTableCell>Approved Rate</StyledTableCell>
							<StyledTableCell>Unit</StyledTableCell>
							<StyledTableCell> Qty</StyledTableCell>
							<StyledTableCell>Requested Qty</StyledTableCell>
							<StyledTableCell>Approved Qty</StyledTableCell>
							<StyledTableCell align="center">Total</StyledTableCell>
							<StyledTableCell align="center">Approved Total</StyledTableCell>
							{props.TITLE === "Approve PO" && poDetails?.status !== "Approved" && poDetails?.status !== "Rejected" && 
							<>
								{/* <StyledTableCell align="center">Approve For Current PO</StyledTableCell> */}
								<StyledTableCell align="center">Update Std Rate Card</StyledTableCell>
								<StyledTableCell align="center">Action</StyledTableCell>
							</>
							}
						</TableRow>
					</TableHead>
					<TableBody>
						{poDetails?.materials?.map((data, i) => <StyledTableRow StyledTableRow key={i + 1}>
							<StyledTableCell>{i + 1}</StyledTableCell>
							<StyledTableCell>{data?.materialId?.code}</StyledTableCell>
							<StyledTableCell>{data?.scopeId?.name}</StyledTableCell>
							<StyledTableCell>{data?.status}</StyledTableCell>
							<StyledTableCell>{data?.pricePerUnit}</StyledTableCell>
							{props.TITLE === "Approve PO" ? <StyledTableCell><TextField className={classes.textField} value={newRequestedRate[i]} onChange={(e) => handelRequestPriceChange(e, i)} type="number" variant="outlined" />	</StyledTableCell> : <StyledTableCell>{data?.originallyRequestedRate}</StyledTableCell>}
							<StyledTableCell>{data?.approvedRate || data?.requestedPrice }</StyledTableCell>
							<StyledTableCell>{data?.unitId?.name}</StyledTableCell>
							<StyledTableCell>{data?.quantity}</StyledTableCell>
							{props.TITLE === "Approve PO" ? <StyledTableCell><TextField className={classes.textField} value={newRequestedQty[i]} onChange={(e) => handleRequestQunatityChange(e, i)} type="number" variant="outlined" />	</StyledTableCell> : <StyledTableCell>{data?.originallyRequestedQty}</StyledTableCell>}
							<StyledTableCell>{data?.approvedQuantity || data?.requestedQuantity}</StyledTableCell> 
							<StyledTableCell>{parseFloat(data?.requestedQuantity * data?.requestedPrice).toFixed(2)}</StyledTableCell>
							<StyledTableCell>{parseFloat(data?.approvedQuantity * data?.approvedRate !== 0 ? data?.approvedQuantity * data?.approvedRate : data?.requestedQuantity * data?.requestedPrice ).toFixed(2)}</StyledTableCell>
							{/* {props.TITLE === "Approve PO" && data?.status !== "Approved" && data?.status !== "Rejected" && poDetails?.status !== "Approved" && poDetails?.status !== "Rejected" &&
								<StyledTableCell>
									{data?.requestedPrice != data?.pricePerUnit && <FormControlLabel onChange={(e) => approveCurrentPoCheckHandler(e, i)} control={<Checkbox checked={data?.approveForCurrentPo} />} label="" />}
								</StyledTableCell>} */}
							{props.TITLE === "Approve PO" && data?.status !== "Approved" && data?.status !== "Rejected" && poDetails?.status !== "Approved" && poDetails?.status !== "Rejected" &&
								<StyledTableCell>
									{+newRequestedRate[i] > +data?.pricePerUnit && <FormControlLabel onChange={(e)=>updateStdRateCheckHandler(e,i)} control={<Checkbox checked={data?.updateStandardRateOfMaterial}/>} label="" />}
								</StyledTableCell>}
							{props.TITLE === "Approve PO" && data?.operationHeadApproved === false && data?.status !== "Approved" && data?.status !== "Rejected" && poDetails?.status !== "Approved" && poDetails?.status !== "Rejected" && <StyledTableCell> <div className="approve-reject-btn">
								<button
									onClick={() => approveProcurement(poDetails?._id, i)}
									className='approve-btn'
									disabled={vendorMissing}
									title={vendorMissing ? "Select a vendor first" : ""}
									style={vendorMissing ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
								>Approve</button>
								<button
									onClick={() => rejectProcurement(poDetails?._id, i)}
									className='reject-btn'
									disabled={vendorMissing}
									title={vendorMissing ? "Select a vendor first" : ""}
									style={vendorMissing ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
								>Reject</button>
							</div>
							</StyledTableCell>}
						</StyledTableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<div>
				<Dialog open={deleteOpenModel} onClose={() => setDeleteOpenModel(false)} aria-labelledby="form-dialog-title" maxWidth='sm'>
					<DialogTitle id="form-dialog-title">This Action cannot be Reverted!</DialogTitle>
					<DialogActions>
						<Button variant="outlined" onClick={() => setDeleteOpenModel(false)} color="secondary">
							Cancel
						</Button>
						<Button onClick={deletePoConfirm} variant="outlined" color="primary">
							Submit
						</Button>
					</DialogActions>
				</Dialog>
			</div>
			<div>
				<Dialog
					open={rejectOpenModel}
					onClose={() => setRejectOpenModel(false)}
					aria-labelledby="form-dialog-title"
					maxWidth="sm"
					fullWidth
				>
					<DialogContent>
						<DialogContentText>Enter Remarks For Rejection</DialogContentText>
						<TextField
							id="remarks"
							name="Remarks"
							label="Add Remarks"
							variant="outlined"
							type="text"
							value={remarks}
							onChange={(e) => setRemarks(e.target.value)}
							fullWidth
						/>
					</DialogContent>
					<DialogActions>
						<Button
							variant="outlined"
							onClick={() => setRejectOpenModel(false)}
							color="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={rejectProcurementConfirm}
							variant="outlined"
							color="primary"
						>
							Submit
						</Button>
					</DialogActions>
				</Dialog>
			</div>
		</div >
	)
}

export default connect(null, { setLoading, setSnackInfo })(PoDetailsModal)
